import { Form } from 'antd'
import { useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { API } from '@/api'
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { Button, message, Modal } from '@/components'
import { PAYMENT_CURRENCY, PAYMENT_TYPE } from '@/consts/billing'
import { TeamAppContext } from '@/contexts/TeamContext'
import { useTeamStore } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import type { Currency, FormValues, RechargeModalProps, Step } from '@/types/billing'
import {
  isValidFeeAmount,
  transformInvoiceFormToApiData,
  validateInvoiceForm,
} from '@/utils/billing'

import { useInvoiceForm } from '../hooks/useInvoiceForm'
import { useInvoiceFormState } from '../hooks/useInvoiceFormState'
import { usePaymentCalculation } from '../hooks/usePaymentCalculation'
import { usePlatformBalance } from '../hooks/usePlatformBalance'
import { useRechargeForm } from '../hooks/useRechargeForm'
import { CharityModal } from './CharityModal'
import { InvoiceStep } from './InvoiceStep'
import { RechargeStep } from './RechargeStep'
import { usePaymentProcessing } from './usePaymentProcessing'

export function ExchargeModal(props: RechargeModalProps) {
  const { onOk, onClose, purpose = 'general' } = props
  const { processPayment } = usePaymentProcessing()
  const { t } = useTranslation('billing')

  const teamApp = useContext(TeamAppContext)
  const isTeamContext = teamApp !== null
  const allowPlatformBalanceTransfer = isTeamContext && purpose !== 'subscription'

  // Team store for getting team resID
  const teamStore = useTeamStore()
  const userStore = useUserStore()

  // Platform balance for team context (transfer credits from platform to team)
  const { platformBalance, isLoading: isPlatformBalanceLoading } = usePlatformBalance(
    allowPlatformBalanceTransfer,
  )

  // Step management
  const [currentStep, setCurrentStep] = useState<Step>('recharge')
  const [loading, setLoading] = useState(false)
  const [savingInvoice, setSavingInvoice] = useState(false)
  const [processingPayment, setProcessingPayment] = useState(false)

  // Use custom hooks
  const {
    form,
    feeFocus,
    setFeeFocus,
    inputValue,
    setInputValue,
    feePoint,
    setFeePoint,
    currency,
    setCurrency,
    customFeeDescription,
    setCustomFeeDescription,
    initialValues,
    debouncedAutoCorrect,
    resetCustomFeeOnRadioFocus,
  } = useRechargeForm()

  const {
    invoiceForm,
    selectedCharity,
    showCharityModal,
    setShowCharityModal,
    handleCharitySelect,
  } = useInvoiceForm()

  // New state management (will gradually replace old logic)
  const { state: invoiceState, actions: invoiceActions } = useInvoiceFormState()

  const {
    paymentAmount,
    setPaymentAmount,
    isCalculating,
    setIsCalculating,
    feeInCurrency,
    debouncedFetchPaymentAmount,
  } = usePaymentCalculation(currency)

  // Form value change handler
  const onValueChange = (values: Partial<FormValues>) => {
    const { fee, currency: newCurrency, customFee } = values || {}

    if ('fee' in values) {
      setFeePoint(fee || 0)
      setIsCalculating(true)
      if (fee !== undefined) {
        form.setFieldsValue({ customFee: undefined })
        debouncedFetchPaymentAmount(fee)
      }
    }

    if ('customFee' in values) {
      setFeePoint(customFee || 0)
      setIsCalculating(true)
      if (customFee !== undefined) {
        form.setFieldsValue({ fee: undefined })
      }
    }

    if ('currency' in values && newCurrency) {
      setCurrency(newCurrency as Currency)
      setIsCalculating(true)

      if (newCurrency === 'USD') {
        form.setFieldsValue({ type: PAYMENT_TYPE.PAYPAL })
      }

      if (newCurrency === 'TWD') {
        form.setFieldsValue({ type: PAYMENT_TYPE.NEWEBPAY })
      }

      // When switching currency, recalculate the currently selected fee
      // Prefer using the current form value to avoid using potentially outdated feePoint state
      const currentFee = form.getFieldValue('fee')
      const currentCustomFee = form.getFieldValue('customFee')
      const activeFee = currentFee || currentCustomFee
      if (activeFee && activeFee > 0) {
        // call API to recalculate the payment amount
        const newPayCurrency = PAYMENT_CURRENCY[newCurrency as Currency]
        setIsCalculating(true)
        API.getExchangeRate({
          credit_cent: activeFee,
          pay_currency: newPayCurrency,
        })
          .then((res) => {
            if (res.code === 0) {
              setPaymentAmount(res.amount)
            }
            setIsCalculating(false)
          })
          .catch(() => {
            setIsCalculating(false)
          })
      }
    }
  }

  // Navigation handlers
  const _onNext = async () => {
    if (currentStep === 'recharge') {
      const formRes = await form.validateFields()
      if (formRes) {
        const paymentType = form.getFieldValue('type')
        if (paymentType === PAYMENT_TYPE.NEWEBPAY) {
          setCurrentStep('invoice')
        } else {
          _onOK()
        }
      }
    } else {
      const invoiceRes = await invoiceForm.validateFields()
      if (invoiceRes) {
        await _onInvoiceNext()
      }
    }
  }

  const _onInvoiceNext = async () => {
    try {
      // Step 1: Save invoice title information
      setSavingInvoice(true)
      setLoading(true)

      // Use new state management
      const { formData } = invoiceState

      const invoiceApiData = transformInvoiceFormToApiData(
        formData.invoiceType,
        formData,
        selectedCharity || undefined,
      )

      const invoiceResult = await API.updateInvoiceTitle({ ezpay: invoiceApiData })

      if (invoiceResult.code !== 0) return

      setSavingInvoice(false)

      // Step 2: Invoice save successful, continue with payment flow
      setProcessingPayment(true)
      await _onOK()
    } catch (error) {
      console.error('Invoice save error:', error)
    } finally {
      setSavingInvoice(false)
      setProcessingPayment(false)
      setLoading(false)
    }
  }

  const _onBack = () => {
    if (currentStep === 'invoice') {
      setCurrentStep('recharge')
    }
  }

  // Button text helper
  const getButtonText = () => {
    if (savingInvoice) return t('rechargeModal.savingInvoice')
    if (processingPayment) return t('rechargeModal.processingPayment')
    if (currentStep === 'recharge') {
      return form.getFieldValue('type') === PAYMENT_TYPE.NEWEBPAY
        ? t('rechargeModal.nextButton')
        : t('rechargeModal.rechargeButton')
    }
    return t('rechargeModal.nextButton')
  }

  // Button disabled helper - uses new state management
  const isButtonDisabled = () => {
    if (loading) return true

    if (currentStep === 'recharge') {
      return isCalculating || !Number.parseInt(`${feePoint}`) || !isValidFeeAmount(feePoint)
    }

    if (currentStep === 'invoice') {
      // Use new state management for validation
      const { formData, matchedOrganization, errors, loadingStates } = invoiceState

      // Special handling for donation type
      if (formData.invoiceType === 'donation') {
        // Check if there's an input but no matched organization or search error
        const hasDonationInput = formData.charityCode && formData.charityCode.trim()
        const hasValidMatch = matchedOrganization && !errors.searchError
        const isSearching = loadingStates.searchingCharity

        // Disable if: has input but no valid match, or currently searching, or loading
        if (hasDonationInput && (!hasValidMatch || isSearching)) {
          return true
        }

        // If no input at all, also disable
        if (!hasDonationInput) {
          return true
        }
      }

      // Check if there are any validation errors
      const hasValidationErrors = Object.values(errors.validationErrors).some(
        (error) => error && error.trim() !== '',
      )

      // If there are validation errors, disable the button
      if (hasValidationErrors) {
        return true
      }

      const formValidation = validateInvoiceForm(formData.invoiceType, {
        name: formData.name,
        email: formData.email,
        mobileBarcode: formData.mobileBarcode,
        businessId: formData.businessId,
        businessName: formData.businessName,
        charityCode: formData.charityCode || '',
      })

      return !formValidation || loadingStates.fetchingTitle
    }

    return false
  }

  // Handle platform balance transfer (team context only)
  // Uses team token - backend automatically transfers from platform account to current team
  const _onTransferCredits = async () => {
    try {
      setLoading(true)
      setProcessingPayment(true)

      // Get the transfer amount from form
      const fee = form.getFieldValue('fee')
      const customFee = form.getFieldValue('customFee')
      const transferAmount = fee || customFee

      if (!transferAmount || transferAmount <= 0) {
        message.error(t('rechargeModal.invalidAmount'))
        return
      }

      // Convert credits to cents (1 credit = 100 cents)
      const transferAmountCent = transferAmount * 100

      // Check if user has enough platform balance
      if (transferAmountCent > platformBalance) {
        message.error(t('rechargeModal.insufficientBalance'))
        return
      }

      // Get team resID
      const teamResID = teamStore.teamTokenData?.teamResID
      if (!teamResID) {
        message.error(t('rechargeModal.teamNotFound'))
        return
      }

      // Call transfer API with team token (default)
      const res = await API.transferCreditsToTeam({
        res_id: teamResID,
        amount_cent: transferAmountCent,
      })

      if (res.code === 0) {
        message.success(t('rechargeModal.transferSuccess'))
        // Refresh user balance
        userStore.getAccount()
        onOk()
      } else {
        message.error(res.msg || t('rechargeModal.transferFailed'))
      }
    } catch (error) {
      console.error('Transfer credits error:', error)
      message.error(t('rechargeModal.transferFailed'))
    } finally {
      setLoading(false)
      setProcessingPayment(false)
    }
  }

  // Payment submission
  const _onOK = async () => {
    try {
      const rechargeFormValues = form.getFieldsValue(true)
      const paymentType = rechargeFormValues.type

      if (purpose === 'subscription' && paymentType === PAYMENT_TYPE.GLOWS_PLATFORM_BALANCE) {
        message.error(t('rechargeModal.subscriptionTransferNotAllowed'))
        return
      }

      // Handle platform balance transfer
      if (paymentType === PAYMENT_TYPE.GLOWS_PLATFORM_BALANCE) {
        await _onTransferCredits()
        return
      }

      const invoiceFormValues = invoiceForm.getFieldsValue()

      // Determine return URL based on environment and context
      const isProduction = import.meta.env.VITE_ENV === 'production'
      const baseUrl = isProduction ? 'https://a.glows.ai' : 'https://as.glows.ai'
      const path = isTeamContext ? '/api/team/billing' : '/api/billing'
      const return_url = `${baseUrl}${path}`

      const paymentData = {
        ...rechargeFormValues,
        fee: paymentAmount * 100, // cent
        return_url,
        invoice: invoiceFormValues,
      }
      const res = await API.payment(paymentData)

      if (res.code === 0 && typeof res.url === 'string') {
        processPayment(res.url, paymentType, onOk)
      } else {
        message.error(res.msg || t('rechargeModal.paymentFailed'))
      }
    } catch (error) {
      message.error(t('rechargeModal.paymentFailedRetry'))
    }
  }

  // Effects
  useEffect(() => {
    resetCustomFeeOnRadioFocus()
  }, [resetCustomFeeOnRadioFocus])

  useEffect(() => {
    if (feePoint > 0) {
      debouncedFetchPaymentAmount(feePoint)
    }
  }, [feePoint]) // only depend on feePoint, avoid calling API when currency is switched

  useEffect(() => {
    if (initialValues.fee > 0) {
      debouncedFetchPaymentAmount(initialValues.fee)
    }
  }, [initialValues.fee]) // only depend on initialValues.fee

  // Sync invoice form initial values
  useEffect(() => {
    if (currentStep === 'invoice') {
      // Use setTimeout to ensure the form has been rendered and initialValues are applied
      setTimeout(() => {
        // Force trigger onValuesChange to sync the state
        invoiceForm.validateFields().catch(() => {})
      }, 100)
    }
  }, [currentStep, invoiceForm])

  return (
    <>
      <Modal
        className="w-[640px] h-[760px]"
        open={true}
        onClose={() => {
          if (!loading) {
            onClose()
          }
        }}
      >
        <Modal.Header>
          <div className="flex items-center gap-8">
            {currentStep === 'invoice' && (
              <button onClick={_onBack} className="p-3 bg-panel-gray-100 rounded-lg">
                <ArrowLeftIcon className="w-6 h-6 text-secondary" />
              </button>
            )}
            <Trans
              ns="billing"
              i18nKey={currentStep === 'recharge' ? 'rechargeModal.title' : 'invoiceModal.title'}
            >
              {currentStep === 'recharge' ? 'Recharge' : 'Invoice Information'}
            </Trans>
          </div>
        </Modal.Header>

        <Modal.Body className="max-h-none">
          {currentStep === 'recharge' ? (
            <Form
              layout="vertical"
              form={form}
              initialValues={initialValues}
              onValuesChange={onValueChange}
              validateTrigger="onBlur"
              scrollToFirstError
            >
              <RechargeStep
                form={form}
                loading={loading}
                feeFocus={feeFocus}
                inputValue={inputValue}
                customFeeDescription={customFeeDescription}
                onFeeFocusChange={setFeeFocus}
                onInputValueChange={setInputValue}
                onCustomFeeDescriptionChange={setCustomFeeDescription}
                debouncedFetchPaymentAmount={debouncedFetchPaymentAmount}
                debouncedAutoCorrect={debouncedAutoCorrect}
                setFeePoint={setFeePoint}
                setPaymentAmount={setPaymentAmount}
                setIsCalculating={setIsCalculating}
                balanceKind={purpose === 'subscription' ? 'recharged' : 'total'}
                allowPlatformBalanceTransfer={allowPlatformBalanceTransfer}
                platformBalance={platformBalance}
                isPlatformBalanceLoading={isPlatformBalanceLoading}
              />
            </Form>
          ) : (
            <Form
              layout="vertical"
              form={invoiceForm}
              validateTrigger="onBlur"
              scrollToFirstError
              onValuesChange={() => {
                // Form change handling now managed by useInvoiceFormState
              }}
            >
              <InvoiceStep
                formData={invoiceState.formData}
                loadingStates={invoiceState.loadingStates}
                matchedOrganization={invoiceState.matchedOrganization}
                errors={invoiceState.errors}
                onInvoiceTypeChange={invoiceActions.handleInvoiceTypeChange}
                onFieldChange={invoiceActions.handleFieldChange}
                onShowCharityModal={() => setShowCharityModal(true)}
              />
            </Form>
          )}

          <div className="flex items-end justify-end gap-4 absolute bottom-0 left-8 right-8 bg-panel-white pb-8">
            {currentStep === 'recharge' && (
              <div className="flex flex-col flex-grow">
                <div className="h4 text-highlight gap-2">{feeInCurrency}</div>
                <div className="t-xs text-tertiary">
                  <Trans ns="billing" i18nKey="rechargeModal.estimatedAmountPayable">
                    Estimated amount payable
                  </Trans>
                </div>
              </div>
            )}
            {currentStep === 'invoice' && <div className="flex-grow"></div>}

            {currentStep === 'invoice' && (
              <Button type="outline" size="md" onClick={_onBack} disabled={loading}>
                {t('invoiceModal.back')}
              </Button>
            )}

            {currentStep === 'recharge' && (
              <Button
                type="outline"
                size="md"
                onClick={() => {
                  if (!loading) {
                    onClose()
                  }
                }}
                className="min-w-[88px]"
              >
                <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
              </Button>
            )}
            <Button
              type="secondary"
              size="md"
              disabled={isButtonDisabled()}
              className="min-w-[88px]"
              onClick={_onNext}
            >
              {getButtonText()}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <CharityModal
        open={showCharityModal}
        selectedCharity={selectedCharity}
        onClose={() => setShowCharityModal(false)}
        onSelect={(charity) => {
          // Use new state management
          invoiceActions.handleCharitySelection(charity)
          // Also update old state for backward compatibility
          handleCharitySelect(charity)
          setShowCharityModal(false)
        }}
      />
    </>
  )
}
